import { OrderPlanningRowAction, OrderPlanningRowActionProps, ReasonMap } from "./OrderPlanningRowAction";
import { Button } from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { getDispatchControlValue } from "@mcleod/general";
import { LoadMatch } from "../../LoadMatch";
import { RowDriverOnlyFields } from "../../../asset/src/models/autogen/AutogenModelDriverOnly";
import { OrderPlanningEquipment } from "../opsmgmt/OrderPlanningEquipment";
import { OrderPlanningTimeline } from "../timeline/OrderPlanningTimeline";
import { RowOrderPlanningProfile } from "../../models/ModelOrderPlanningProfile";

interface ExecuteProps extends OrderPlanningRowActionProps {
    layout: OrderPlanningEquipment|OrderPlanningTimeline;
}

export class RowActionLoadMatch extends OrderPlanningRowAction {
    override getButton(): Button {
        return this.toolPanel.buttonLoadMatch;
    }

    override getButtonId(): string {
        return "buttonLoadMatch";
    }

    override getAdditionalProps(): Partial<ExecuteProps> {
        return {
            layout: this.toolPanel.actionsLayout.getParentLayout() as OrderPlanningEquipment | OrderPlanningTimeline,
            ...this.getFindNearAfterSlideoutProp()
        }
    }

    override getFieldsUsedInThisFile(): string[] {
        return ["tractor", "driver1_id", "driver1_name_first", "driver1_name_last", "driver1_name", "driver1_type",
            "driver1_disable_driver_choice", "move_status", "order_id", "np_equip_group_id", "equip_group_id"];
    }

    override actionValidateSelection(selectedRows: ModelRow[], reasonMap: ReasonMap): void {
        const modelRow = selectedRows[0];
        const driverType = modelRow.get("driver1_type", null);
        if (!driverType) {
            this.addReason(reasonMap, "missing_driver", "Selected row does not have an associated driver.");
        } else {
            const loadMatchSetting = getDispatchControlValue("choice_driver_types");
            const driverTypeCompatible = loadMatchSetting === "B" || loadMatchSetting === driverType;
            const driverLmDisabled = modelRow.getBoolean("driver1_disable_driver_choice", true);

            if (!driverTypeCompatible) {
                this.addReason(reasonMap, "incompatible_driver_type", "LoadMatch is not compatible with the selected driver type.");
            } else if (driverLmDisabled) {
                this.addReason(reasonMap, "disabled_driver_choice", "LoadMatch is disabled for the selected driver.");
            }
        }
    }

    override execute(props: ExecuteProps) {
        const modelRow = props.selectedRows[0]?.data as ModelRow;
        if (modelRow == null)
            return;

        const driverData = this.extractDriverData(modelRow, "driver1");
        if (driverData == null) return;

        const tractorId = modelRow.get("tractor");
        const movementId = modelRow.get("movement_id", null);
        const orderId = modelRow.get("order_id", null);
        const equipmentGroupId = modelRow.get("D" === modelRow.get("move_status", "") ? "np_equip_group_id" : "equip_group_id");
        const profileRow = props.layout["profile"] as RowOrderPlanningProfile;

        LoadMatch.openSlideout(
            { driverData, tractorId, equipmentGroupId, profileRow, movementId, orderId },
            { doAfterSlideOut: props.afterSlideout});
    }

    private extractDriverData(modelRow: ModelRow, fieldPrefix: string): Partial<RowDriverOnlyFields> {
        if (modelRow.isNull(fieldPrefix + "_id")) {
            return null;
        }
        return {
            id: modelRow.get(fieldPrefix + "_id"),
            first_name: modelRow.get(fieldPrefix + "_name_first"),
            name: modelRow.get(fieldPrefix + "_name_last"),
            full_name: modelRow.get(fieldPrefix + "_name"),
        }
    }

    static override canShowAction(): boolean {
        const loadMatch = getDispatchControlValue("choice_driver_types");
        return loadMatch != null && "N" !== loadMatch
    }
}
